// import { ChangeEvent } from "@dashboard/hooks/useForm"; // Deprecated
import { useLazyQuery } from "@apollo/client";
import { Multiselect } from "@dashboard/components/Combobox";
import { DashboardModal } from "@dashboard/components/Modal";
import {   Box,
  Button,
  Input,
Option ,
  Text,
} from "@saleor/macaw-ui-next";
import React, { useEffect,useState } from "react";

import { SHIPPING_RESTRICTIONS_PRODUCT_LIST_QUERY } from "../queries";

// US States data
const US_STATES = [
  { name: "Alabama", abbreviation: "AL" },
  { name: "Alaska", abbreviation: "AK" },
  { name: "American Samoa", abbreviation: "AS" },
  { name: "Arizona", abbreviation: "AZ" },
  { name: "Arkansas", abbreviation: "AR" },
  { name: "California", abbreviation: "CA" },
  { name: "Colorado", abbreviation: "CO" },
  { name: "Connecticut", abbreviation: "CT" },
  { name: "Delaware", abbreviation: "DE" },
  { name: "District Of Columbia", abbreviation: "DC" },
  { name: "Federated States Of Micronesia", abbreviation: "FM" },
  { name: "Florida", abbreviation: "FL" },
  { name: "Georgia", abbreviation: "GA" },
  { name: "Guam", abbreviation: "GU" },
  { name: "Hawaii", abbreviation: "HI" },
  { name: "Idaho", abbreviation: "ID" },
  { name: "Illinois", abbreviation: "IL" },
  { name: "Indiana", abbreviation: "IN" },
  { name: "Iowa", abbreviation: "IA" },
  { name: "Kansas", abbreviation: "KS" },
  { name: "Kentucky", abbreviation: "KY" },
  { name: "Louisiana", abbreviation: "LA" },
  { name: "Maine", abbreviation: "ME" },
  { name: "Marshall Islands", abbreviation: "MH" },
  { name: "Maryland", abbreviation: "MD" },
  { name: "Massachusetts", abbreviation: "MA" },
  { name: "Michigan", abbreviation: "MI" },
  { name: "Minnesota", abbreviation: "MN" },
  { name: "Mississippi", abbreviation: "MS" },
  { name: "Missouri", abbreviation: "MO" },
  { name: "Montana", abbreviation: "MT" },
  { name: "Nebraska", abbreviation: "NE" },
  { name: "Nevada", abbreviation: "NV" },
  { name: "New Hampshire", abbreviation: "NH" },
  { name: "New Jersey", abbreviation: "NJ" },
  { name: "New Mexico", abbreviation: "NM" },
  { name: "New York", abbreviation: "NY" },
  { name: "North Carolina", abbreviation: "NC" },
  { name: "North Dakota", abbreviation: "ND" },
  { name: "Northern Mariana Islands", abbreviation: "MP" },
  { name: "Ohio", abbreviation: "OH" },
  { name: "Oklahoma", abbreviation: "OK" },
  { name: "Oregon", abbreviation: "OR" },
  { name: "Palau", abbreviation: "PW" },
  { name: "Pennsylvania", abbreviation: "PA" },
  { name: "Puerto Rico", abbreviation: "PR" },
  { name: "Rhode Island", abbreviation: "RI" },
  { name: "South Carolina", abbreviation: "SC" },
  { name: "South Dakota", abbreviation: "SD" },
  { name: "Tennessee", abbreviation: "TN" },
  { name: "Texas", abbreviation: "TX" },
  { name: "Utah", abbreviation: "UT" },
  { name: "Vermont", abbreviation: "VT" },
  { name: "Virgin Islands", abbreviation: "VI" },
  { name: "Virginia", abbreviation: "VA" },
  { name: "Washington", abbreviation: "WA" },
  { name: "West Virginia", abbreviation: "WV" },
  { name: "Wisconsin", abbreviation: "WI" },
  { name: "Wyoming", abbreviation: "WY" },
];

export interface RestrictionFormData {
  title: string;
  states: Option[];
  products: Option[];
  productMessage: string;
  checkoutMessage: string;
  zipCodes: Option[];
  isActive: boolean;
}

interface Restriction {
  id: string;
  title: string;
  states: string[];
  products: { id: string; name: string }[];
  productMessage: string;
  checkoutMessage: string;
  zipCodes: number[];
  isActive: boolean;
}

interface ShippingRestrictionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: RestrictionFormData) => void;
  loading?: boolean;
  editingRestriction?: Restriction | null;
}

export const ShippingRestrictionModal: React.FC<ShippingRestrictionModalProps> = ({
  open,
  onClose,
  onSave,
  loading = false,
  editingRestriction = null,
}) => {
  const [formData, setFormData] = useState<RestrictionFormData>({
    title: "",
    states: [],
    products: [],
    productMessage: "",
    checkoutMessage: "",
    zipCodes: [],
    isActive: true,
  });

  const [stateOptions, setStateOptions] = useState<Option[]>([]);
  const [productOptions, setProductOptions] = useState<Option[]>([]);

  const [fetchProducts, { loading: productsLoading, error: productsError }] = useLazyQuery(SHIPPING_RESTRICTIONS_PRODUCT_LIST_QUERY, {
    onCompleted: (data) => {
      if (data?.products?.edges) {
        const options = data.products.edges.map((edge: any) => ({
          label: edge.node.name,
          value: edge.node.id,
        }));

        setProductOptions(options);
      } else {
        setProductOptions([]);
      }
    },
    onError: () => {
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  });

  // Initialize state options
  useEffect(() => {
    const stateOpts = US_STATES.map((state) => ({
      label: `${state.name} (${state.abbreviation})`,
      value: state.abbreviation,
    }));

    setStateOptions(stateOpts);
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingRestriction && open) {
      
      // Map states to full state options
      const stateOpts = editingRestriction.states.map(stateCode => {
        const stateInfo = US_STATES.find(s => s.abbreviation === stateCode);

        return stateInfo ? {
          label: `${stateInfo.name} (${stateInfo.abbreviation})`,
          value: stateInfo.abbreviation
        } : { label: stateCode, value: stateCode };
      });
      
      setFormData({
        title: editingRestriction.title,
        states: stateOpts,
        products: editingRestriction.products.map(p => ({ label: p.name, value: p.id })),
        productMessage: editingRestriction.productMessage,
        checkoutMessage: editingRestriction.checkoutMessage,
        zipCodes: editingRestriction.zipCodes.map(zip => ({ label: zip.toString(), value: zip.toString() })),
        isActive: editingRestriction.isActive ?? true,
      });
    }
  }, [editingRestriction, open]);

  // Load initial products when modal opens and clear when closes
  useEffect(() => {
    if (open) {
      
      // Always clear and reload products to ensure fresh data
      setProductOptions([]);
      
      const variables = {
        first: 20,
        search: "",
        channel: "default-channel",
      };
      
      // Use setTimeout to ensure the modal is fully rendered
      const timeoutId = setTimeout(() => {
        fetchProducts({
          variables,
        });
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      // Always clear product options when modal closes
      setProductOptions([]);

      if (!editingRestriction) {
        setFormData({
          title: "",
          states: [],
          products: [],
          productMessage: "",
          checkoutMessage: "",
          zipCodes: [],
          isActive: true,
        });
      }
    }
  }, [open, fetchProducts, editingRestriction]);

  const handleInputChange = (field: keyof RestrictionFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleMultiselectChange = (field: keyof RestrictionFormData) => (
    event: { target: { value: Option[] } }
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleZipCodeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement;
      const value = input.value.trim();
      
      if (value && !formData.zipCodes.find(zip => zip.value === value)) {
        // Add the zip code directly
        const newZipCode = { label: value, value: value };

        setFormData((prev) => ({
          ...prev,
          zipCodes: [...prev.zipCodes, newZipCode],
        }));
        // Clear the input
        input.value = '';
        event.preventDefault();
      }
    }
  };

  const handleProductSearch = (query: string) => {
    fetchProducts({
      variables: {
        first: 20,
        search: query || "",
        channel: "default-channel",
      },
    });
  };

  // Zip code validation happens automatically in the Multiselect component with allowCustomValues

  const handleSave = () => {
    onSave(formData);
  };

  if (!open) return null;

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="md">
        <DashboardModal.Header>
          {editingRestriction ? "Edit Restriction" : "Add Restriction"}
        </DashboardModal.Header>

        <Box padding={6}>

          <Box display="flex" flexDirection="column" gap={4}>
            {/* Title Field */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                Title
              </Text>
              <Input
                placeholder="Restriction title"
                value={formData.title}
                onChange={handleInputChange("title")}
                size="medium"
              />
            </Box>

            {/* States Multiselect */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                States
              </Text>
              <Multiselect
                name="states"
                options={stateOptions}
                value={formData.states}
                onChange={handleMultiselectChange("states")}
                fetchOptions={() => {}} // States are static
                placeholder="Select states"
              />
            </Box>

            {/* Products Multiselect */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                Products
              </Text>
              <Multiselect
                name="products"
                options={productOptions}
                value={formData.products}
                onChange={handleMultiselectChange("products")}
                fetchOptions={handleProductSearch}
                loading={productsLoading}
                placeholder="Search and select products"
                alwaysFetchOnFocus={true}
                size="medium"
                error={!!productsError}
                helperText={productsError ? `Error loading products: ${productsError.message}` : undefined}
              />
            </Box>

            {/* Product Message */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                Product Message
              </Text>
              <Input
                placeholder="Product message"
                value={formData.productMessage}
                onChange={handleInputChange("productMessage")}
                size="medium"
              />
            </Box>

            {/* Checkout Message */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                Checkout Message
              </Text>
              <Input
                placeholder="Checkout message"
                value={formData.checkoutMessage}
                onChange={handleInputChange("checkoutMessage")}
                size="medium"
              />
            </Box>

            {/* Zip Codes */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                Zip Codes
              </Text>
              <Multiselect
                name="zipCodes"
                options={[]}
                value={formData.zipCodes}
                onChange={handleMultiselectChange("zipCodes")}
                fetchOptions={() => {}} // No fetch needed for custom values
                allowCustomValues={true}
                placeholder="Type zip code and press Enter..."
                size="medium"
                alwaysFetchOnFocus={false}
                onKeyDown={handleZipCodeKeyDown}
              />
            </Box>

            {/* Active Status Checkbox */}
            <Box>
              <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }));
                  }}
                  style={{ margin: 0 }}
                />
                <Text size={3} fontWeight="medium">
                  Active
                </Text>
                <Text size={2}>
                  {formData.isActive ? "This restriction is active" : "This restriction is inactive"}
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="space-between" marginTop={6}>
            <Button variant="secondary" onClick={onClose}>
              Back
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={loading || !formData.title}
            >
              {editingRestriction ? "Update" : "Save"}
            </Button>
          </Box>
        </Box>
      </DashboardModal.Content>
    </DashboardModal>
  );
};